// Grundbausteine der Oberfläche. Große Tippflächen (mind. 48 px), weil
// Kunden das Handy oft mit einer Hand halten, während sie fotografieren.

import { useEffect, useId, useRef, type ReactNode } from "react";

export function Button(props: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "quiet" | "danger";
  disabled?: boolean;
  type?: "button" | "submit";
  wide?: boolean;
}) {
  const { variant = "primary", wide = true } = props;
  return (
    <button
      type={props.type ?? "button"}
      className={`btn btn-${variant}${wide ? " btn-wide" : ""}`}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {props.children}
    </button>
  );
}

export function Field(props: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
  type?: "text" | "tel" | "email" | "date" | "number";
  inputMode?: "text" | "numeric" | "tel" | "email";
  autoComplete?: string;
  placeholder?: string;
  hint?: string;
  error?: string | null;
  maxLength?: number;
  min?: string;
}) {
  const id = useId();
  return (
    <div className={`field${props.error ? " field-error" : ""}`}>
      <label htmlFor={id}>{props.label}</label>
      <input
        id={id}
        type={props.type ?? "text"}
        inputMode={props.inputMode}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        min={props.min}
        value={props.value ?? ""}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.hint && <p className="hint">{props.hint}</p>}
      {props.error && <p className="error">{props.error}</p>}
    </div>
  );
}

export function Choice<T extends string | number | boolean>(props: {
  label: string;
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  error?: string | null;
}) {
  return (
    <div className={`field${props.error ? " field-error" : ""}`} role="radiogroup" aria-label={props.label}>
      <span className="label">{props.label}</span>
      <div className="choice">
        {props.options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={props.value === o.value}
            className={props.value === o.value ? "on" : ""}
            onClick={() => props.onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      {props.error && <p className="error">{props.error}</p>}
    </div>
  );
}

/** Auswahlliste für optionale Angaben: "Keine Angabe" setzt den Wert zurück auf null. */
export function OptionalSelect<T extends string>(props: {
  label: string;
  value: T | null;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (v: T | null) => void;
  hint?: string;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{props.label}</label>
      <select id={id} value={props.value ?? ""} onChange={(e) => props.onChange(e.target.value === "" ? null : (e.target.value as T))}>
        <option value="">Keine Angabe</option>
        {props.options.map((o) => (
          <option key={o} value={o}>
            {props.labels[o]}
          </option>
        ))}
      </select>
      {props.hint && <p className="hint">{props.hint}</p>}
    </div>
  );
}

export function Check(props: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; hint?: string }) {
  const id = useId();
  return (
    <div className="check">
      <input id={id} type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      <label htmlFor={id}>
        {props.children}
        {props.hint && <span className="hint">{props.hint}</span>}
      </label>
    </div>
  );
}

export function Stepper(props: { label: string; value: number; min?: number; max?: number; onChange: (v: number) => void }) {
  const { min = 0, max = 9 } = props;
  return (
    <div className="stepper">
      <span>{props.label}</span>
      <div className="stepper-ctl">
        <button type="button" aria-label={`${props.label} weniger`} disabled={props.value <= min} onClick={() => props.onChange(props.value - 1)}>
          −
        </button>
        <output aria-live="polite">{props.value}</output>
        <button type="button" aria-label={`${props.label} mehr`} disabled={props.value >= max} onClick={() => props.onChange(props.value + 1)}>
          +
        </button>
      </div>
    </div>
  );
}

export function Dialog(props: { title: string; children?: ReactNode; actions: ReactNode; onClose?: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, []);
  return (
    <div className="overlay" onClick={props.onClose}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={props.title} ref={ref} onClick={(e) => e.stopPropagation()}>
        <h2>{props.title}</h2>
        {props.children}
        <div className="dialog-actions">{props.actions}</div>
      </div>
    </div>
  );
}

/** Untere Leiste mit Zurück/Weiter, bleibt beim Scrollen sichtbar. */
export function Footer(props: { onBack?: () => void; onNext?: () => void; nextLabel?: string; nextDisabled?: boolean; children?: ReactNode }) {
  return (
    <div className="footer">
      {props.children}
      <div className="footer-row">
        {props.onBack && (
          <Button variant="quiet" wide={false} onClick={props.onBack}>
            Zurück
          </Button>
        )}
        {props.onNext && (
          <Button onClick={props.onNext} disabled={props.nextDisabled}>
            {props.nextLabel ?? "Weiter"}
          </Button>
        )}
      </div>
    </div>
  );
}
